import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Heart, Leaf, Users, Phone, Mail, MapPin, Calendar, ArrowRight, Sparkles, Shield, BookOpen, MessageCircle, Brain, Target, Bed, Apple, Flower2, Home, Globe, Coffee, Zap, Activity } from 'lucide-react';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import heroImage from '@/assets/hero_image.jpg';

const ImmuneSupportPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Immune Support Currimundi | Chinese Medicine";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Natural immune support and seasonal wellness with Chinese Medicine in Currimundi. Strengthen immunity, prevent illness & boost vitality. Book online or visit our Sunshine Coast clinic.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Immune Support Currimundi | Chinese Medicine");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Natural immune support and seasonal wellness with Chinese Medicine in Currimundi. Strengthen immunity, prevent illness & boost vitality. Book online or visit our Sunshine Coast clinic.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/immune-support");
    }
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/hero_image.jpg");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Immune Support Currimundi | Chinese Medicine");
    }
    const schema = {
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      "name": "Women and The Way - Immune Support with Chinese Medicine",
      "image": "https://womenandtheway.com.au/hero_image.jpg",
      "description": "Natural immune support and seasonal wellness with Chinese Medicine in Currimundi. Strengthen immunity, prevent illness & boost vitality.",
      "url": "https://womenandtheway.com.au/immune-support",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "postalCode": "4551",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "telephone": "0415537784",
      "email": "womenandtheway@gmail.com",
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      "serviceType": "Immune Support, Seasonal Wellness, Preventive Care, Chinese Medicine, Acupuncture, Herbal Medicine",
      "areaServed": {
        "@type": "Place",
        "name": "Sunshine Coast, Queensland, Australia"
      },
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Immune Support & Seasonal Wellness
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Strengthen your body's natural defences - gently, wisely, and seasonally.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  A strong immune system is essential for overall health and vitality. At Women and the Way, we support your body's natural defenses using Chinese Medicine to strengthen immunity and promote resilience.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Whether you're frequently getting sick, recovering from illness, or simply want to maintain optimal immune function, our treatments help support your body's natural defense mechanisms. Our approach combines the wisdom of Traditional Chinese Medicine with modern understanding of immune health. Many of our clients also benefit from our <a href="/digestion-gut-health" className="text-sage-600 hover:text-sage-800 underline transition-colors">digestive health services</a> and <a href="/sleep-support" className="text-sage-600 hover:text-sage-800 underline transition-colors">sleep support</a>.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={heroImage} 
                  alt="Immune support and seasonal wellness with Chinese medicine on the Sunshine Coast" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Immune Support Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Shield className="w-8 h-8 text-sage-600" />
                Why Immune Support Matters in Chinese Medicine
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                In Traditional Chinese Medicine, immunity is deeply connected to your Lung Qi (the organ that governs boundaries), your Spleen Qi (digestion and absorption), and your Kidneys (deep reserves of energy). Seasonal wind, dampness, or internal cold can disrupt the flow of Qi and make us more susceptible to illness.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                At Women and the Way, we offer acupuncture, herbal medicine, and lifestyle guidance to help you feel strong, steady, and supported - especially during seasonal changes, cold and flu season, post-viral recovery, or times of stress and depletion.
              </p>
            </div>
          </div>
        </section>

        {/* When to Seek Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                When to Seek Support
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Seasonal Transitions</h3>
                  </div>
                  <p className="text-sage-700">Strengthen Wei Qi, prevent colds/flus, and adapt your energy to the shift in temperature</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Cold & Flu Season</h3>
                  </div>
                  <p className="text-sage-700">Support your immunity naturally, reduce symptom severity, and recover faster</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Target className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Post-Viral Recovery</h3>
                  </div>
                  <p className="text-sage-700">Rebuild Qi and Lung strength after COVID, glandular fever, or prolonged illness</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Coffee className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Chronic Immune Depletion</h3>
                  </div>
                  <p className="text-sage-700">Address low-grade fatigue, poor resilience, or frequent colds/infections</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Allergies & Sinus Issues</h3>
                  </div>
                  <p className="text-sage-700">Calm the immune system and clear lingering Damp, Phlegm, and Wind</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Stress-Induced Immune Suppression</h3>
                  </div>
                  <p className="text-sage-700">Nourish the Spleen and restore balance after burnout, grief, or overwork</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Seasonal Wellness In-Clinic or Online
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* In-Clinic Support */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Acupuncture</h3>
                  </div>
                  <p className="text-sage-700 mb-4">To strengthen Wei Qi, support the Lungs, and regulate your energy</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Custom Herbal Medicine</h3>
                  </div>
                  <p className="text-sage-700 mb-4">For prevention, acute symptoms, or long-term resilience</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Activity className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Seasonal Wellness Guidance</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Lifestyle and nutrition advice aligned with the current season</p>
                </CardContent>
              </Card>

              {/* Online Sessions */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <MessageCircle className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Telehealth Consultations</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Immune support guidance and seasonal wellness from the comfort of your home</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Herbal Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Custom formulas mailed to your door for ongoing immune support</p>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Wellness Resources</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Seasonal guides, immune-boosting recipes, and wellness tools</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Strengthen your body's natural defences - gently, wisely, and seasonally.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
              onClick={handleBooking}
              disabled={isLoading}
            >
              {isLoading ? 'Booking...' : 'Book Your Session'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default ImmuneSupportPage; 